import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Cookie, Mail, Phone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import logoFull from '@/assets/logo-full.svg';
import { LanguageSelector } from '@/components/LanguageSelector';
import { openCookieSettings } from '@/lib/cookieConsent';
import clusterForLogistics from '@/assets/partners/cluster-for-logistics.svg.asset.json';
import technoport from '@/assets/partners/technoport.png.asset.json';

/**
 * Partner logos shown in the footer.
 * Drop the files in `src/assets/partners/` and add an entry here:
 *   { name: 'Partner', logo: partnerLogo, url: 'https://partner.com' }
 */
export const partners: {
  name: string;
  logo: string;
  url?: string;
  sizeClass?: string;
  compactSizeClass?: string;
}[] = [
  {
    name: 'Cluster for Logistics',
    logo: clusterForLogistics.url,
    url: 'https://www.clusterforlogistics.lu/welcome',
  },
  {
    name: 'Technoport – Automobility Incubator',
    logo: technoport.url,
    url: 'https://technoport.lu/',
    sizeClass: 'h-[104px] md:h-[130px]',
    compactSizeClass: 'h-10 md:h-12',

  },
];


interface AppFooterProps {
  className?: string;
  /** 'public' = partners in a dedicated band above legal links; 'app' = partners as a column on the right */
  variant?: 'public' | 'app';
}

export const AppFooter = ({ className = '', variant = 'public' }: AppFooterProps) => {
  const { t } = useTranslation();


  const { data: contact } = useQuery({
    queryKey: ['support-contact'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_contacts')
        .select('email, phone')
        .limit(1)
        .maybeSingle();
      if (error) return { email: 'info@myvelox.eu', phone: null as string | null };
      return data ?? { email: 'info@myvelox.eu', phone: null as string | null };
    },
    staleTime: 1000 * 60 * 30,
  });

  const legalLinks = [
    { to: '/about', label: t('footer.links.about') },
    { to: '/terms-of-service', label: t('footer.links.terms') },
    { to: '/privacy-policy', label: t('footer.links.privacy') },
    { to: '/legal-notice', label: t('footer.links.legalNotice') },
    { to: '/cookie-policy', label: t('footer.links.cookiePolicy') },
  ];

  const renderLogo = (partner: (typeof partners)[number], sizeClass: string) =>
    partner.url ? (
      <a
        key={partner.name}
        href={partner.url}
        target="_blank"
        rel="noopener noreferrer"
        className="opacity-90 hover:opacity-100 transition-all duration-200"
      >
        <img src={partner.logo} alt={partner.name} className={`${sizeClass} object-contain`} loading="lazy" />
      </a>
    ) : (
      <img
        key={partner.name}
        src={partner.logo}
        alt={partner.name}
        className={`${sizeClass} object-contain grayscale opacity-70`}
        loading="lazy"
      />
    );

  if (variant === 'app') {
    const shortLinks = [
      { to: '/about', label: t('footer.short.about') },
      { to: '/terms-of-service', label: t('footer.short.terms') },
      { to: '/privacy-policy', label: t('footer.short.privacy') },
      { to: '/legal-notice', label: t('footer.short.legalNotice') },
      { to: '/cookie-policy', label: t('footer.short.cookies') },
    ];

    return (
      <footer className={`border-t border-[#DBE2EA] bg-white ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-[#5A6B7A] order-3 sm:order-1">{t('footer.copyright')}</p>

          <nav className="order-1 sm:order-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            {shortLinks.map((link, index) => (
              <span key={link.to} className="flex items-center gap-x-3">
                {index > 0 && (
                  <span aria-hidden className="text-[#DBE2EA]">
                    ·
                  </span>
                )}
                <Link
                  to={link.to}
                  className="text-xs text-[#5A6B7A] hover:text-[#0880AE] transition-colors duration-200"
                >
                  {link.label}
                </Link>
              </span>
            ))}
            <span aria-hidden className="text-[#DBE2EA]">
              ·
            </span>
            <button
              onClick={openCookieSettings}
              className="text-xs text-[#5A6B7A] hover:text-[#0880AE] transition-colors duration-200"
            >
              {t('footer.links.cookieSettings')}
            </button>
            {contact?.email && (
              <>
                <span aria-hidden className="text-[#DBE2EA]">
                  ·
                </span>
                <a
                  href={`mailto:${contact.email}`}
                  className="text-xs text-[#5A6B7A] hover:text-[#0880AE] transition-colors duration-200"
                >
                  {contact.email}
                </a>
              </>
            )}
            {contact?.phone && (
              <>
                <span aria-hidden className="text-[#DBE2EA]">
                  ·
                </span>
                <a
                  href={`tel:${contact.phone.replace(/\s/g, '')}`}
                  className="text-xs text-[#5A6B7A] hover:text-[#0880AE] transition-colors duration-200"
                >
                  {contact.phone}
                </a>
              </>
            )}

          </nav>

          {partners.length > 0 && (
            <div className="order-2 sm:order-3 flex items-center justify-center gap-4">
              {partners.map((partner) =>
                renderLogo(partner, partner.name.toLowerCase().includes('technoport') ? 'h-10' : 'h-8'),
              )}
            </div>
          )}

        </div>
      </footer>
    );
  }


  return (
    <footer className={`border-t border-[#DBE2EA] bg-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Partners band (public pages only) */}
        {partners.length > 0 && (
          <div className="mb-12 pb-8 border-b border-[#DBE2EA]">
            <h3 className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-6 text-center">
              {t('footer.partnersTitle')}
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {partners.map((partner) => renderLogo(partner, partner.sizeClass ?? 'h-16 md:h-20'))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">

          {/* Brand */}
          <div className="space-y-3">
            <img src={logoFull} alt="Velox" className="h-8" loading="lazy" />
            <p className="text-sm text-[#5A6B7A] max-w-xs">{t('footer.tagline')}</p>
          </div>

          {/* Legal */}
          <div className="space-y-3">
            <h3 className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('footer.legalTitle')}
            </h3>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-[#2D3748] hover:text-[#0880AE] transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={openCookieSettings}
                  className="inline-flex items-center gap-2 text-sm text-[#2D3748] hover:text-[#0880AE] transition-colors duration-200"
                >
                  <Cookie size={16} className="text-[#0880AE]" />
                  {t('footer.links.cookieSettings')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-3">
            <h3 className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('footer.contactTitle')}
            </h3>
            <ul className="space-y-2">
              {contact?.email && (
                <li>
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex items-center gap-2 text-sm text-[#2D3748] hover:text-[#0880AE] transition-colors duration-200"
                  >
                    <Mail size={16} className="text-[#0880AE]" />
                    {contact.email}
                  </a>
                </li>
              )}
              {contact?.phone && (
                <li>
                  <a
                    href={`tel:${contact.phone}`}
                    className="inline-flex items-center gap-2 text-sm text-[#2D3748] hover:text-[#0880AE] transition-colors duration-200"
                  >
                    <Phone size={16} className="text-[#0880AE]" />
                    {contact.phone}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-[#DBE2EA] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#5A6B7A]">{t('footer.copyright')}</p>
          <LanguageSelector />
        </div>
      </div>
    </footer>
  );
};


export default AppFooter;
